import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlarmClock, AlertTriangle, Bell, CalendarClock, CheckCheck, CreditCard, FileWarning, Flame, Loader2, PiggyBank } from 'lucide-react';
import notificationService from '@services/notificationService';
import { usePermissions } from '@hooks/usePermissions';
import { useActivityModal } from '@contexts/ActivityModalContext';
import type { AppNotification, NotificationType } from '@/types/planning';
import { formatDateBR } from '@/utils/formatters';

const POLL_MS = 60 * 1000;

const TYPE_STYLE: Record<NotificationType, { icon: React.ReactNode; bubble: string; label: string }> = {
  overdue: { icon: <AlertTriangle size={14} className="text-red-600" />, bubble: 'bg-red-100', label: 'Atrasada' },
  due_today: { icon: <AlarmClock size={14} className="text-amber-600" />, bubble: 'bg-amber-100', label: 'Vence hoje' },
  critical: { icon: <Flame size={14} className="text-rose-600" />, bubble: 'bg-rose-100', label: 'Crítica' },
  doc_expiring: { icon: <FileWarning size={14} className="text-violet-600" />, bubble: 'bg-violet-100', label: 'Documento vencendo' },
  bill_due: { icon: <CreditCard size={14} className="text-sky-600" />, bubble: 'bg-sky-100', label: 'Fatura a vencer' },
  budget_exceeded: { icon: <PiggyBank size={14} className="text-amber-700" />, bubble: 'bg-amber-100', label: 'Orçamento estourado' },
  schedule_pending: { icon: <CalendarClock size={14} className="text-orange-600" />, bubble: 'bg-orange-100', label: 'Agendar pagamento' },
};

/** Fase 7: alerta "conta a agendar" abre Lançamentos já filtrado nas pendentes (mesmo destino do e-mail). */
const SCHEDULE_PENDING_PATH = '/finance/transactions?agendamento=pendente';

/**
 * Sino de alertas do header. O contador (GET /notifications?unread=1) é consultado a cada 60s e a
 * cada mudança de rota; a lista completa (GET /notifications) só quando o dropdown abre. Clicar
 * num alerta marca como lido e abre a atividade no modal global (ou, em `schedule_pending`, Lançamentos
 * filtrado nas contas a agendar). Falhas são silenciosas — o sino
 * é não-crítico e não pode derrubar a tela.
 */
export const NotificationBell: React.FC = () => {
  const { checkPermission, isLoading: permissionsLoading } = usePermissions();
  const canRead = !permissionsLoading && checkPermission('notificacoes.read').hasPermission;
  const { openActivity, version } = useActivityModal();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [loadingList, setLoadingList] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const list = await notificationService.getAll(true);
      setUnread(list.length);
    } catch {
      // Silent fail — notification is non-critical
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      setItems(await notificationService.getAll());
    } catch {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Rota nova ou atividade alterada → recontar.
  useEffect(() => {
    if (!canRead) return;
    fetchUnread();
  }, [canRead, location.pathname, version, fetchUnread]);

  // Polling a cada 60s (pausa com a aba oculta).
  useEffect(() => {
    if (!canRead) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnread();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [canRead, fetchUnread]);

  if (!canRead) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClick = async (n: AppNotification) => {
    setOpen(false);
    if (!n.read_at) {
      try {
        await notificationService.markRead(n.id);
      } catch {
        /* ignore */
      }
      fetchUnread();
    }
    if (n.type === 'schedule_pending') {
      navigate(SCHEDULE_PENDING_PATH);
      return;
    }
    const activityId = n.ref_type === 'activity' ? n.ref_id : n.activity_id;
    if (activityId) openActivity(activityId);
  };

  const handleReadAll = async () => {
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev?.map((n) => ({ ...n, read_at: n.read_at ?? 'agora' })) ?? prev);
      fetchUnread();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="relative">
      <button
        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors relative"
        onClick={toggle}
        title="Alertas"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[70vh] flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-900">Alertas</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {unread === 0 ? 'Nenhum alerta não lido' : `${unread} não lido${unread === 1 ? '' : 's'}`}
              </p>
            </div>
            {unread > 0 && (
              <button
                onClick={handleReadAll}
                className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 whitespace-nowrap"
              >
                <CheckCheck size={14} /> Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="overflow-y-auto custom-scrollbar">
            {loadingList && items === null && (
              <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin mr-2" /> Carregando…
              </div>
            )}
            {items !== null && items.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">Nenhum alerta</div>
            )}
            {items?.map((n) => {
              const style = TYPE_STYLE[n.type] ?? TYPE_STYLE.due_today;
              return (
                <button
                  key={n.id}
                  className={clsx(
                    'w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-t border-slate-100 first:border-t-0',
                    !n.read_at && 'bg-emerald-50/40',
                  )}
                  onClick={() => handleClick(n)}
                >
                  <div className="flex items-start gap-3">
                    <div className={clsx('p-1.5 rounded-full mt-0.5', style.bubble)}>{style.icon}</div>
                    <div className="min-w-0 flex-1">
                      <p className={clsx('text-sm text-slate-900', !n.read_at && 'font-medium')}>{n.message}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {style.label} · {formatDateBR(n.notification_date)}
                      </p>
                    </div>
                    {!n.read_at && <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {open && <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />}
    </div>
  );
};
